import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, OnDesktop, OnMobile, Sidebar } from '@hopskipdrive/react-components';
import { HsdLogo, HsdLogoText } from '@rideiq/shared/components/Icons';

import { analyticsOpenedHelpCenter } from '../../../api/services/Analytics';
import { useAuthMutations } from '../../../auth/hooks/useAuthMutations';
import { useAuthQuery } from '../../../auth/hooks/useAuthQuery';
import { actions, Can, subjects, useAbility } from '../../../contexts/AbilityContext';
import { getImpersonationExpiresAt } from '../../../utils/helpers/storage';
import { RouterLink } from '../Links';
import { colorTokens } from '../variables';

import navItemsConfig from './navItemsConfig';
import { sidebarIcons } from './sidebarIcons';
import { SidebarNavIcon } from './SidebarNavIcon';
import { SidebarProfileItem } from './SidebarProfileItem';
import { BottomDivider, SidebarNavContainer, SidebarNavMenu, SidebarNavMenuLinkItem, TopDivider } from './styles';

export const SidebarNav: React.FC = () => {
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  return (
    <Sidebar
      hasImpersonationHeader={!!getImpersonationExpiresAt()}
      sx={{ padding: '18px 6px', background: colorTokens.navigation.background }}
    >
      <OnMobile>
        <Sidebar.MobileBar onMenuClick={() => setOpenMobileMenu(true)}>
          <RouterLink to="/" aria-label="HopSkipDrive logo">
            <Logo isMobile={true} />
          </RouterLink>
        </Sidebar.MobileBar>
        <Sidebar.MobileDrawer
          open={openMobileMenu}
          onCloseButton={() => setOpenMobileMenu(false)}
          onClose={() => setOpenMobileMenu(false)}
          PaperProps={{ style: { backgroundColor: colorTokens.navigation.background } }}
        >
          <MenuItems onMenuItemClick={() => setOpenMobileMenu(false)} isMobile={true} />
        </Sidebar.MobileDrawer>
      </OnMobile>
      <OnDesktop>
        <RouterLink to="/" aria-label="HopSkipDrive logo">
          <Logo />
        </RouterLink>
        <TopDivider />
        <MenuItems onMenuItemClick={() => null} isMobile={false} />
      </OnDesktop>
    </Sidebar>
  );
};

const MenuItems = ({ onMenuItemClick, isMobile }) => {
  const { user, isOnsiteStaffUser, isImpersonating } = useAuthQuery();
  const { handleLogout, handleStopImpersonation } = useAuthMutations();
  const { pathname } = useLocation();
  const abilities = useAbility();
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`;
  const accountName = `${user?.accountName || ''}`;
  const currentPath = pathname.match(/^(?<resource>\/\w+)/)
    ? pathname.match(/^(?<resource>\/\w+)/)?.groups?.resource
    : '/';
  const isDailyQueuePage = currentPath === '/rides';

  return (
    <SidebarNavContainer
      as={isMobile ? 'nav' : undefined}
      aria-label={isMobile ? 'Primary navigation' : undefined}
      $mobile={isMobile}
    >
      <SidebarNavMenu data-testid="sidebar-menu" style={{ minHeight: 375 }}>
        {isOnsiteStaffUser ? (
          <SidebarNavMenuLinkItem
            data-testid="page-link"
            active={isDailyQueuePage}
            aria-current={isDailyQueuePage ? 'page' : undefined}
            component={RouterLink}
            to="/rides/daily_queue"
            onClick={onMenuItemClick}
            $mobile={isMobile}
          >
            <SidebarNavIcon icon={sidebarIcons.rides} />
            <Sidebar.MenuLinkText>Daily Queue</Sidebar.MenuLinkText>
          </SidebarNavMenuLinkItem>
        ) : (
          navItemsConfig()
            .filter((config) => abilities.can(config.allowedAction, config.allowedSubject) && config.enabled)
            .map((navItem) => {
              const isCurrentPage = navItem.route === currentPath;

              return (
                <SidebarNavMenuLinkItem
                  key={navItem.route}
                  data-testid="page-link"
                  active={isCurrentPage}
                  aria-current={isCurrentPage ? 'page' : undefined}
                  component={RouterLink}
                  to={navItem.route}
                  onClick={onMenuItemClick}
                  $mobile={isMobile}
                >
                  <SidebarNavIcon icon={navItem.icon} />
                  <Sidebar.MenuLinkText>{navItem.title}</Sidebar.MenuLinkText>
                </SidebarNavMenuLinkItem>
              );
            })
        )}
      </SidebarNavMenu>

      <SidebarNavMenuLinkItem
        data-testid="help-center-link"
        mt="auto"
        onClick={() => analyticsOpenedHelpCenter()}
        href={window.env?.RIDEIQ_ZENDESK_HELP_CENTER_URL}
        target={'_blank'}
        $mobile={isMobile}
      >
        <SidebarNavIcon icon={sidebarIcons.helpCenter} />
        <Sidebar.MenuLinkText>Help Center</Sidebar.MenuLinkText>
      </SidebarNavMenuLinkItem>
      <BottomDivider />
      <SidebarProfileItem userName={userName} accountName={accountName} isMobile={isMobile}>
        <Box display="flex" flexDirection="column" gap="24px">
          {!isOnsiteStaffUser && (
            <Can I={actions.read} a={subjects.settings}>
              <RouterLink variant="body1" to="/settings/account_details" onClick={onMenuItemClick}>
                Account settings
              </RouterLink>
            </Can>
          )}
          {isImpersonating ? (
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key == ' ') {
                  handleStopImpersonation();
                }
              }}
              onClick={handleStopImpersonation}
            >
              Stop Impersonating
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key == ' ') {
                  handleLogout();
                }
              }}
              onClick={handleLogout}
            >
              Logout
            </div>
          )}
        </Box>
      </SidebarProfileItem>
    </SidebarNavContainer>
  );
};

const Logo = ({ isMobile = false }) => {
  return isMobile ? (
    <Box display="flex" justifyContent="center" width="74vw">
      <HsdLogoText data-testid="sidebar-logo" width={136} height={22} alt="HopSkipDrive logo" />
    </Box>
  ) : (
    <HsdLogo data-testid="sidebar-logo" width={48} height={48} alt="HopSkipDrive logo" />
  );
};
